const ENDPOINT = "https://experiment-worker.christopherbeagles.workers.dev/sdk";
const TIMEOUT = 1500;

export class VarianceExperiment extends HTMLElement {
  static experimentCachePromise: Promise<any[]> | null = null;

  static get observedAttributes() {
    return ["id", "client-id", "preview-mode", "variant-to-preview"];
  }

  experiments: any[] = [];
  hasSent = false;

  connectedCallback() {
    if (this.ensureValidPreview()) return;

    this.previewMode ? this.render() : this.fetchExperiments();
  }

  attributeChangedCallback(name: string) {
    if (this.ensureValidPreview()) return;

    if (this.previewMode) {
      this.render();
      return;
    }

    if (name === "client-id") {
      this.fetchExperiments();
    }
  }

  /* --- Getters --- */
  get clientId() {
    return this.getAttribute("client-id") ?? "";
  }
  get previewMode() {
    return this.getAttribute("preview-mode") === "true";
  }
  get experimentId() {
    return this.getAttribute("id") ?? "";
  }
  get variantToPreview() {
    return this.getAttribute("variant-to-preview");
  }

  /* --- Validation --- */
  private ensureValidPreview() {
    if (!this.previewMode) return false;

    const missing = !this.variantToPreview;
    if (missing) {
      console.error(
        `[variance-experiment] preview-mode="true" requires variant-to-preview`,
      );
    }
    return missing;
  }

  /* --- Fetching --- */
  async fetchExperiments() {
    if (this.previewMode) {
      this.render();
      return;
    }

    if (!VarianceExperiment.experimentCachePromise) {
      VarianceExperiment.experimentCachePromise = this.fetchAllExperiments();
    }

    try {
      this.experiments = await VarianceExperiment.experimentCachePromise;
    } catch (err) {
      console.error(err);
      this.experiments = [];
    } finally {
      this.render();
    }
  }

  async fetchAllExperiments(): Promise<any[]> {
    const url = new URL(ENDPOINT);
    url.searchParams.set("clientId", this.clientId);

    const controller = new AbortController();
    const timeoutHandle = setTimeout(() => controller.abort(), TIMEOUT);

    try {
      const res = await fetch(url.toString(), { signal: controller.signal });
      return await res.json();
    } catch (err) {
      console.error(err);
      return [];
    } finally {
      clearTimeout(timeoutHandle);
    }
  }

  /* --- Analytics --- */
  sendImpression(experimentId: string, variantId: string) {
    if (this.hasSent) return;
    this.hasSent = true;

    const payload = {
      event: "experiment_impression",
      experiment_id: experimentId,
      variant_id: variantId,
    };

    window.dataLayer = window.dataLayer || [];
    window.gtag =
      window.gtag ||
      function () {
        window.dataLayer.push(arguments);
      };

    window.gtag("event", "experiment_impression", payload);
    window.dataLayer.push(payload);
  }

  /* --- Rendering --- */
  render() {
    if (this.previewMode) {
      return this.renderPreview(this.variantToPreview!);
    }

    const experiment = this.experiments.find((e) => e.id === this.experimentId);
    if (!experiment) {
      return this.clear();
    }

    const variantId = experiment?.variant?.id ?? null;
    if (variantId) {
      this.sendImpression(experiment.id, variantId);
    }

    this.renderVariant(variantId);
  }

  private renderPreview(variantId: string) {
    this.hasSent = true; // block impressions
    this.renderVariant(variantId);
  }

  private renderVariant(variantId: string | null) {
    if (!variantId) return this.clear();

    // Capture original children before wiping
    const child = Array.from(this.children).find(
      (el) =>
        el instanceof HTMLElement &&
        el.tagName.toLowerCase() === "variance-variant" &&
        el.getAttribute("id") === variantId,
    );

    this.innerHTML = "";

    if (child) {
      this.appendChild(child.cloneNode(true));
    }

    this.style.display = "block";
  }

  private clear() {
    this.innerHTML = "";
    this.style.display = "none";
  }
}

export class VarianceVariant extends HTMLElement {}

customElements.define("variance-experiment", VarianceExperiment);
customElements.define("variance-variant", VarianceVariant);
